import { useRef, type FC, type ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import type { SharedValue } from 'react-native-reanimated';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';

const styles = StyleSheet.create({
  container: { width: '100%' },
  front: { backfaceVisibility: 'hidden', position: 'absolute', width: '100%' },
  back: { backfaceVisibility: 'hidden' }
});

type Props = {
  direction?: 'x' | 'y';
  front: ReactNode;
  back: ReactNode;
  isFlipped: SharedValue<boolean>;
  duration?: number;
};

export const FlipView: FC<Props> = ({
  direction = 'y',
  front,
  back,
  isFlipped,
  duration = 500
}) => {
  const isInitialMount = useRef<boolean>(true);
  const frontHeight = useSharedValue<number>(0);
  const backHeight = useSharedValue<number>(0);

  const frontStyle = useAnimatedStyle(() => {
    const spinValue = interpolate(Number(isFlipped.value), [0, 1], [0, 180]);
    const rotateValue = withTiming(`${spinValue}deg`, { duration });

    return {
      zIndex: isFlipped.value ? 1 : 2,
      transform: [
        direction === 'x' ? { rotateX: rotateValue } : { rotateY: rotateValue }
      ]
    };
  });

  const backStyle = useAnimatedStyle(() => {
    const spinValue = interpolate(Number(isFlipped.value), [0, 1], [180, 360]);
    const rotateValue = withTiming(`${spinValue}deg`, { duration });

    return {
      zIndex: isFlipped.value ? 2 : 1,
      transform: [
        direction === 'x' ? { rotateX: rotateValue } : { rotateY: rotateValue }
      ]
    };
  });

  const derivedHeight = useDerivedValue(() => {
    const desiredHeight = isFlipped.value
      ? backHeight.value
      : frontHeight.value;

    // set initial height without timing on render
    if (desiredHeight > 0 && isInitialMount.current) {
      isInitialMount.current = false;
      return desiredHeight;
    }

    return withTiming(desiredHeight, {
      duration: duration * 0.75
    });
  });

  const height = useAnimatedStyle(() => ({
    height: derivedHeight.value || 'auto'
  }));

  return (
    <Animated.View style={height}>
      <Animated.View
        style={[styles.front, frontStyle]}
        onLayout={(e) => frontHeight.set(e.nativeEvent.layout.height)}
      >
        {front}
      </Animated.View>
      <Animated.View
        style={[styles.back, backStyle]}
        onLayout={(e) => backHeight.set(e.nativeEvent.layout.height)}
      >
        {back}
      </Animated.View>
    </Animated.View>
  );
};
